// src/components/Gallery/MediaComponents.js
import React, { useState, useEffect, useRef } from 'react';
import { loadImage } from '../../utils/imageLoader';

// Basic Dynamic Image Component
export const DynamicImage = ({ 
  imagePath, 
  alt, 
  className, 
  style, 
  onClick, 
  loading = "lazy",
  priority = "normal",
  onLoad,
  onError 
}) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (imagePath) {
      setIsLoading(true);
      setError(false);
      
      loadImage(imagePath, priority)
        .then(url => {
          if (url) {
            setImageUrl(url);
            setError(false);
          } else {
            setError(true);
          }
          setIsLoading(false);
        })
        .catch(err => {
          console.error('Error loading image:', err);
          setError(true);
          setIsLoading(false);
          if (onError) onError(err);
        });
    }
  }, [imagePath, onError, priority]);

  const handleImageLoad = () => {
    if (onLoad) onLoad();
  };

  if (isLoading) {
    return (
      <div 
        className={className}
        style={{
          ...style,
          backgroundColor: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px'
        }}
      >
        <div style={{ 
          color: '#6c757d', 
          fontSize: '14px',
          textAlign: 'center'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div 
        className={className}
        style={{
          ...style,
          backgroundColor: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          border: '1px solid #e9ecef'
        }}
      >
        <div style={{ 
          color: '#dc3545', 
          fontSize: '14px',
          textAlign: 'center'
        }}>
          Image not found
        </div>
      </div>
    );
  }

  return (
    <img 
      src={imageUrl}
      alt={alt}
      className={className}
      style={style}
      onClick={onClick}
      onLoad={handleImageLoad}
      loading={loading}
    />
  );
};

// Enhanced Dynamic Image for Lightbox with loading states
export const DynamicImageLightbox = ({ 
  imagePath, 
  alt, 
  style, 
  onClick, 
  onImageLoad,
  priority = "high"
}) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  // Reset all states when imagePath changes
  useEffect(() => {
    setImageUrl(null);
    setIsLoading(true);
    setImageLoaded(false);
    setError(false);
    setShowLoader(false);

    if (imagePath) {
      // Show loader after 200ms delay to avoid flash for fast-loading images
      const loaderTimer = setTimeout(() => {
        if (isLoading && !imageLoaded) {
          setShowLoader(true);
        }
      }, 200);

      loadImage(imagePath, priority)
        .then(url => {
          if (url) {
            setImageUrl(url);
            setError(false);
          } else {
            setError(true);
          }
          setIsLoading(false);
          // Don't clear loader until image is fully loaded
        })
        .catch(err => {
          console.error('Error loading image:', err);
          setError(true);
          setIsLoading(false);
          setShowLoader(false);
          clearTimeout(loaderTimer);
        });

      return () => clearTimeout(loaderTimer);
    }
  }, [imagePath, priority]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setShowLoader(false); // Hide loader once image is fully loaded
    if (onImageLoad) onImageLoad();
  };

  // Show loading state until we have the image URL and it's not in error state
  if ((isLoading || !imageUrl) && !error) {
    return (
      <div style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(248, 249, 250, 0.8)',
        minHeight: '200px',
        borderRadius: '8px'
      }}>
        {showLoader && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px'
          }}>
            {/* Spinning loader */}
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #e9ecef',
              borderTop: '3px solid #495057',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <div style={{
              color: '#6c757d',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              Loading image...
            </div>
          </div>
        )}
        
        {/* CSS keyframes for spinner animation */}
        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        ...style,
        backgroundColor: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#dc3545',
        fontSize: '14px',
        borderRadius: '8px',
        border: '1px solid #e9ecef',
        gap: '8px'
      }}>
        <div>⚠️</div>
        <div>Image not found</div>
      </div>
    );
  }

  return (
    <img 
      src={imageUrl}
      alt={alt}
      style={{
        ...style,
        opacity: imageLoaded ? 1 : 0,
        transition: 'opacity 0.4s ease'
      }}
      onClick={onClick}
      onLoad={handleImageLoad}
      loading="eager"
    />
  );
};

// Dynamic Video Component
export const DynamicVideo = ({ 
  imagePath, 
  className, 
  style, 
  onClick, 
  priority = "normal",
  ...props 
}) => {
  const [videoUrl, setVideoUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (imagePath) {
      loadImage(imagePath, priority)
        .then(url => {
          setVideoUrl(url);
          setIsLoading(false);
        })
        .catch(err => {
          console.error('Error loading video:', err);
          setIsLoading(false);
        });
    }
  }, [imagePath, priority]);

  if (isLoading) {
    return (
      <div 
        className={className}
        style={{
          ...style,
          backgroundColor: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        Loading video...
      </div>
    );
  }

  if (!videoUrl) {
    return (
      <div 
        className={className}
        style={{
          ...style,
          backgroundColor: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        Video not found
      </div>
    );
  }

  return (
    <video
      src={videoUrl}
      className={className}
      style={style}
      onClick={onClick}
      {...props}
    />
  );
};

// Media Component for Grid Display (handles both images and videos)
export const MediaComponent = ({ item, onClick, priority = "normal" }) => {
  if (item.mediaType === 'video') {
    return (
      <div style={{ position: 'relative' }}>
        <DynamicVideo
          imagePath={item.imagePath}
          priority={priority}
          muted
          loop
          playsInline
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            marginBottom: '12px'
          }}
          onMouseEnter={(e) => {
            e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
            e.target.play();
          }}
          onMouseLeave={(e) => {
            e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
            e.target.pause();
          }}
          onClick={onClick}
        />
        {/* Video Play Overlay */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(255,255,255,0.9)',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            color: '#333',
            opacity: 0.8,
            cursor: 'pointer',
            transition: 'opacity 0.3s ease'
          }}
          onClick={onClick}
        >
          ▶
        </div>
      </div>
    );
  }

  return (
    <DynamicImage
      imagePath={item.imagePath}
      alt={item.title}
      priority={priority}
      loading={priority === 'high' ? 'eager' : 'lazy'}
      style={{
        width: '100%',
        height: 'auto',
        display: 'block',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '12px'
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
      }}
    />
  );
};

// Painting Media Component with medium and dimensions overlay
export const PaintingMediaComponent = ({ item, onClick, priority = "normal" }) => {
  return (
    <div className="portfolio-painting-wrapper">
      <DynamicImage
        imagePath={item.imagePath}
        alt={item.title}
        priority={priority}
        loading={priority === 'high' ? 'eager' : 'lazy'}
        onClick={onClick}
      />
      <div className="portfolio-medium-badge">
        {item.medium ? item.medium.split(',')[0] : ''}
        {item.dimensions && (
          <>
            <br />
            <span style={{ 
              fontSize: '9px', 
              opacity: 0.8,
              fontWeight: '400'
            }}>
              {item.dimensions}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default {
  DynamicImage,
  DynamicImageLightbox,
  DynamicVideo,
  MediaComponent,
  PaintingMediaComponent
};